import TravelExpenseRepository from '@/shared/http/repositories/business/travelexpense'
import MiscellaneousExpensesRepository from '@/shared/http/repositories/business/miscellaneousexpenses'
import ActionsForm from '@/shared/mixins/actionsForm'
import { toast } from '@/shared/utils/UIHelper'

// ZC-39/ZC-43 — bloco "Status Despesas por Empresa", usado pelas telas de Viagem e de Diversos.
//
// UM componente para os dois modulos, escolhido pela prop `modulo`. O que muda entre eles e
// pequeno e esta todo em MODULO, abaixo: o repositorio (rota e criterio de consulta diferentes,
// ver CRUDDespesasDiversos.GetVisaoGeralDespesasDiversos) e a sigla do status 2, que e o mesmo
// numero com papel diferente — Transportes em Viagem, Controladoria em Diversos. O rotulo longo
// nao entra aqui: vem do [Description] do enum, pelo backend.
//
// Existe porque a tela opera SEMPRE dentro de uma empresa (a do handoff do SSO, em sessionStorage),
// e depois de ZC-34/35/36 o filtro por empresa dos pools de lote ficou corretamente rígido — o efeito
// colateral é que pendência de outra empresa virou invisível. Medido em 02/09/2026: 1.138 pendentes
// na Zopone contra 96 espalhadas por outras 7 empresas.
//
// Cores: canon do zcloud, nenhuma inventada.
//   #FCB608  DespesasViagem.ColorStatusDespesa:474 (cor de status de despesa do backend)
//   #1A1818  o preto da casa (90 usos em src)
//   #fd7e14  $orange de scss/bootstrap/_variables.scss:41
//   #17a2b8  $cyan — o mesmo azul da classe text-info usada no TableColumn.vue:25
//   #B60000  o vermelho da casa (56 usos)
//   #008144  $theme_color de scss/clients/_common.scss:1
const COR = {
  coordenador: '#FCB608',
  aprovador: '#1A1818',
  tecnico: '#fd7e14',
  diretoria: '#17a2b8',
  vencidas: '#B60000',
  ano: '#008144'
}

const CHAVE_BASE = 'zc39-visao-geral-paineis-v2'
const chavePaineis = modulo => modulo === 'viagem' ? CHAVE_BASE : CHAVE_BASE + '-' + modulo
// O quadro de empresas nasce fechado SEMPRE: seu estado nao vai para o storage nem e lido de la.
const SEM_PERSISTIR = 'empresas'

// O que difere entre os dois modulos. `viagem` e o default para a tela de Viagem, que ja esta em
// producao, nao mudar de comportamento por causa deste card.
const MODULO = {
  viagem: {
    repositorio: TravelExpenseRepository,
    siglaAprovador: 'TRANSP',
    nomeAprovador: 'Transportes'
  },
  diversos: {
    repositorio: MiscellaneousExpensesRepository,
    siglaAprovador: 'CONTR',
    nomeAprovador: 'Controladoria'
  }
}

export default {
  name: 'VisaoGeralDespesas',
  mixins: [ActionsForm],
  props: {
    // A tela mostra SO a grade de empresas. Os 6 cards, o donut e o gasto por moeda continuam no
    // componente porque vao para o submenu Dashboard do ZPag — nada foi apagado.
    apenasEmpresas: { type: Boolean, default: false },
    // `viagem` | `diversos`. Default viagem: a tela de Viagem esta em producao e nao passa a prop.
    modulo: {
      type: String,
      default: 'viagem',
      validator: v => Object.prototype.hasOwnProperty.call(MODULO, v)
    }
  },
  data () {
    return {
      carregando: true,
      erro: null,
      cor: COR,
      dados: null,
      // Estado de recolhido por painel. O `empresas` NAO persiste: o quadro tem de nascer fechado
      // toda vez (pedido do Rodrigo em 03/09). Persistir o "aberto" fazia a preferencia de quem ja
      // tinha aberto vencer o default e o quadro voltava aberto — medido no aceite. Os outros dois
      // seguem persistindo, para o submenu Dashboard. Leitura e escrita em try/catch: em janela
      // privada ou com site data bloqueado o acessor estoura.
      paineisAbertos: { empresas: false, status: true, gasto: true }
    }
  },
  computed: {
    cfg () {
      return MODULO[this.modulo] || MODULO.viagem
    },
    // O gate de role mora no backend: sem ACC_FIN_TRAVELLANEOUS_SEARCH_CONTROLLER_APPROVAL o
    // porEmpresa volta só com a empresa corrente. O front não decide escopo, só desenha o que veio.
    empresas () {
      return this.dados && this.dados.porEmpresa ? this.dados.porEmpresa : []
    },
    outrasEmpresas () {
      return this.empresas.filter(e => !e.ehEmpresaCorrente)
    },
    statusOrdenado () {
      return this.dados && this.dados.porStatus ? this.dados.porStatus : []
    },
    gastoPorMoeda () {
      return this.dados && this.dados.gastoAnoPorMoeda ? this.dados.gastoAnoPorMoeda : []
    },
    totais () {
      return this.dados ? this.dados.totais : null
    },
    // O `<=` nao pode ir na interpolacao do template: o parser do Vue le o `<` como abertura de
    // tag e quebra o build (invalid-first-character-of-tag-name). Fica aqui.
    gastoCaiu () {
      return this.totais && this.totais.gastoDeltaPercentual !== null &&
             this.totais.gastoDeltaPercentual <= 0
    },
    gastoDeltaAbs () {
      if (!this.totais || this.totais.gastoDeltaPercentual === null) return null
      return Math.abs(this.totais.gastoDeltaPercentual).toFixed(1)
    },
    // Somar o total de cada empresa vale porque ninguém tem pendência em duas empresas ao mesmo
    // tempo — medido no aceite em 02/09: 102 pares pessoa-empresa para 102 pessoas distintas.
    totalNoSistema () {
      if (!this.totais) return 0
      return this.totais.colaboradoresTotal + this.totais.colaboradoresOutrasEmpresas
    },
    // Fatias do donut em stroke-dasharray, no mesmo esquema do KpiCard/DashboardChart da Vitrine.
    // A base é a SOMA das fatias, não o total distinto da empresa: quem está parado em dois
    // estágios conta nos dois, e só assim as fatias fecham 100%. Na Zopone os dois números
    // coincidem (88); no Acre não (2 contra 1 pessoa) — por isso a base não pode ser o distinto.
    fatias () {
      const base = this.statusOrdenado.reduce((acc, s) => acc + (s.colaboradores || 0), 0)
      if (!base) return []
      let offset = 25
      return this.statusOrdenado.map(s => {
        const pct = (s.colaboradores * 100) / base
        const fatia = { ...s, pct, dash: `${pct.toFixed(1)} ${(100 - pct).toFixed(1)}`, offset: offset.toFixed(1), cor: this.corDoStatus(s.status) }
        offset -= pct
        return fatia
      })
    }
  },
  mounted () {
    this.restaurarPaineis()
    this.carregar()
  },
  methods: {
    aberto (chave) {
      return this.paineisAbertos[chave] !== false
    },
    alternar (chave) {
      this.paineisAbertos = { ...this.paineisAbertos, [chave]: !this.aberto(chave) }
      if (chave === SEM_PERSISTIR) return
      try {
        const guardar = { ...this.paineisAbertos }
        delete guardar[SEM_PERSISTIR]
        window.localStorage.setItem(chavePaineis(this.modulo), JSON.stringify(guardar))
      } catch (e) { /* sem localStorage a preferencia so nao persiste; a tela funciona igual */ }
    },
    restaurarPaineis () {
      try {
        const salvo = JSON.parse(window.localStorage.getItem(chavePaineis(this.modulo)) || 'null')
        if (salvo && typeof salvo === 'object') {
          // o `empresas` gravado por versoes anteriores e IGNORADO de proposito: o quadro nasce
          // fechado, e nao ha estado salvo que mude isso.
          delete salvo[SEM_PERSISTIR]
          this.paineisAbertos = { ...this.paineisAbertos, ...salvo }
        }
      } catch (e) { /* valor corrompido: fica o default */ }
    },
    // O rotulo vem do [Description] do enum ("AG APROVAÇÃO TRANSPORTES") e e o que muda entre
    // Viagem e Diversos no status 2 — por isso vem do backend. Na legenda o prefixo e redundante
    // (o painel ja se chama "Pendências por status") e faz o texto quebrar em duas linhas.
    rotuloCurto (rotulo) {
      return (rotulo || '').replace(/^AG\.?\s*APROVA[ÇC][ÃA]O\s*/i, '')
    },
    // Siglas dos estagios abaixo do nome da empresa. So os que TEM gente parada entram — card de
    // empresa com quatro zeros vira ruido, e a maioria das empresas so tem um ou dois estagios
    // ocupados (medido no aceite: fora da Zopone, nenhuma passa de dois).
    statusDaEmpresa (e) {
      const tags = [
        { chave: 'x', sigla: 'TÉCN', status: 9, n: e.colaboradoresTecnico, desp: e.pendentesTecnico, cor: COR.tecnico, nome: 'Técnico' },
        { chave: 'c', sigla: 'COORD', status: 1, n: e.colaboradoresCoordenador, desp: e.pendentesCoordenador, cor: COR.coordenador, nome: 'Coordenador' },
        { chave: 't', sigla: this.cfg.siglaAprovador, status: 2, n: e.colaboradoresAprovador, desp: e.pendentesAprovador, cor: COR.aprovador, nome: this.cfg.nomeAprovador },
        { chave: 'd', sigla: 'DIRET', status: 3, n: e.colaboradoresDiretoria, desp: e.pendentesDiretoria, cor: COR.diretoria, nome: 'Diretoria' },
        // VENC não é status do enum: é corte de tempo sobre os quatro. Filtrar por ele exigiria um
        // parâmetro que a busca da tela não tem, então a sigla informa e não navega.
        { chave: 'v', sigla: 'VENC', status: null, n: e.colaboradoresVencidos, desp: e.vencidas, cor: COR.vencidas, nome: 'Parados além do corte' }
      ]
      return tags.filter(x => x.n > 0).map(x => ({
        ...x,
        titulo: x.status === null
          ? `${x.nome}: ${this.formatarNumero(x.n)} colaborador(es), ${this.hintDespesas(x.desp)}`
          : `${x.nome}: ${this.formatarNumero(x.n)} colaborador(es), ${this.hintDespesas(x.desp)} — clique para filtrar`
      }))
    },
    corDoStatus (status) {
      // Os quatro estágios da fila. O status 2 muda de NOME entre os módulos (Transportes em Viagem,
      // Controladoria em Diversos) mas é o mesmo número — o rótulo vem do backend, não daqui.
      if (status === 1) return COR.coordenador
      if (status === 2) return COR.aprovador
      if (status === 3) return COR.diretoria
      if (status === 9) return COR.tecnico
      return '#747474'
    },
    carregar () {
      if (!this.CompanySelected) {
        this.carregando = false
        this.erro = 'Empresa não identificada nesta aba.'
        return
      }
      this.carregando = true
      this.erro = null
      this.cfg.repositorio.GetVisaoGeral(this.CompanySelected)
        .then(response => {
          this.dados = response.data
          this.carregando = false
        })
        .catch(error => {
          this.carregando = false
          this.erro = 'Não foi possível carregar a visão geral.'
          // Loga o CORPO do erro, não só o status — resposta >= 400 sem corpo já custou rodada nesta base.
          console.error('[VisaoGeral ' + this.modulo + '] falhou:', error && error.response ? error.response.data : error)
        })
    },
    // Moeda vem do DADO (localidade da empresa), nunca do locale do navegador — canon LOCALIZACAO §2.
    formatarMoeda (valor, moeda) {
      const iso = moeda || 'BRL'
      try {
        // BRL sai com SIMBOLO (R$), que e o que o operador brasileiro espera. Qualquer outra moeda
        // sai com o CODIGO ISO na frente: em es-CL o CLP renderiza como "$" e na tela se le como
        // dolar. O numero de casas continua vindo do ISO (CLP tem 0, BRL tem 2) — nunca fixo.
        return new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: iso,
          currencyDisplay: iso === 'BRL' ? 'symbol' : 'code'
        }).format(valor || 0)
      } catch (e) {
        return `${iso} ${(valor || 0).toFixed(2)}`
      }
    },
    moedaDaEmpresaCorrente () {
      const emp = this.dados ? this.dados.empresaCorrente : null
      return emp && emp.localidade === 'escl' ? 'CLP' : 'BRL'
    },
    // O card conta COLABORADOR, a mesma unidade da grid da tela (uma linha por pessoa). A contagem
    // de DESPESA desceu para o rodapé: mostrar 380 onde a grid lista 38 linhas já foi lido como
    // defeito na revisão de 02/09 — o Chile tem 87 despesas de 3 pessoas.
    // Só o rótulo: o número já está no kpi-value logo acima.
    unidadePessoas (n) {
      return n === 1 ? 'colaborador parado' : 'colaboradores parados'
    },
    hintDespesas (n) {
      if (!n) return 'sem despesa'
      return n === 1 ? '1 despesa' : this.formatarNumero(n) + ' despesas'
    },
    formatarNumero (n) {
      return new Intl.NumberFormat('pt-BR').format(n || 0)
    },
    // Troca a empresa NA MESMA ABA (decisão do Rodrigo em 03/09 — antes abria nova aba).
    // Mesmo shape que o `consumeHandoffCookie` grava no `main.js:55`, com o `theme` obrigatório:
    // o `App.vue:27` monta a classe do `#app` a partir de `company.theme` (scss/clients/*.scss) e é
    // essa classe que troca o LOGO do header. Sem o campo, cai no default `zopone`.
    // Grava no sessionStorage E no Vuex: o getter de `company` cai no sessionStorage quando o state
    // é null, mas quem já leu o state não reage — o commit é o que faz tema, header e telas virarem.
    trocarEmpresa (empresa) {
      const nova = {
        id: empresa.empresaId,
        companyID: empresa.empresaId,
        nome: empresa.nome,
        codigoZ: empresa.codigoZ,
        localidade: empresa.localidade,
        theme: empresa.theme
      }
      try {
        window.sessionStorage.setItem('company', JSON.stringify(nova))
        this.$store.commit('company/setCompany', nova)
        return true
      } catch (e) {
        console.error('[VisaoGeral] troca de empresa falhou:', e)
        toast.warning('Não foi possível trocar de empresa nesta máquina.')
        return false
      }
    },
    // Clique no CARTÃO: vai para a empresa e traz TUDO, sem filtro de status.
    abrirEmpresa (empresa) {
      if (!empresa.ehEmpresaCorrente && !this.trocarEmpresa(empresa)) return
      this.$emit('navegar', { empresaId: empresa.empresaId, status: null })
      this.carregar()
    },
    // Clique na SIGLA: vai para a empresa e já filtra a grid por aquele estágio.
    // `stopPropagation` é obrigatório — a sigla vive dentro do cartão, que também é clicável.
    abrirStatus (empresa, tag, evento) {
      if (evento) { evento.stopPropagation(); evento.preventDefault() }
      if (!empresa.ehEmpresaCorrente && !this.trocarEmpresa(empresa)) return
      this.$emit('navegar', { empresaId: empresa.empresaId, status: tag.status })
      this.carregar()
    }
  }
}



// WEBPACK FOOTER //
// ./src/views/private/finance/expenses/travelexpensezpag/components/VisaoGeralDespesas/VisaoGeralDespesas.js